import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import DOMPurify from "dompurify";
import { useApi } from "../hooks/useApi";
import api from "../lib/api";
import { Button } from "./ui/button";

export function VersionBadge({ dark = true }) {
  const { data } = useApi("/version");
  if (!data) return null;
  return <span className={`font-mono-accent text-[10px] ${dark ? "text-parchment/40" : "text-muted-foreground"}`} data-testid="version-badge">v{data.version}</span>;
}

const md = (txt) => DOMPurify.sanitize(txt.split("\n").map((l) => {
  if (l.startsWith("### ")) return `<h4 class="mt-4 font-semibold text-crimson">${l.slice(4)}</h4>`;
  if (l.startsWith("## ")) return `<h3 class="font-serif-display text-xl font-bold text-crimson">${l.slice(3)}</h3>`;
  if (l.startsWith("- ")) return `<li class="ml-5 list-disc">${l.slice(2)}</li>`;
  return l.trim() ? `<p class="mt-1">${l}</p>` : "";
}).join(""), { ADD_ATTR: ["class"] });

export function UpgradeBanner() {
  const { data, setData } = useApi("/about");
  if (!data?.show_upgrade_banner) return null;
  const close = async () => { setData({ ...data, show_upgrade_banner: false }); await api.post("/about/seen"); };
  return (
    <div className="mb-5 flex items-center gap-3 rounded-xl border border-palm/40 bg-palm/10 px-4 py-3 text-sm" data-testid="upgrade-banner">
      <Sparkles size={18} className="text-palm" />
      <div className="flex-1"><b>Platform upgraded to v{data.version}.</b> A major release was published{data.release?.previous && ` (from v${data.release.previous})`}. <Link to="/admin/about" className="underline text-palm" onClick={close} data-testid="upgrade-banner-notes">Read the release notes</Link></div>
      <button onClick={close} className="rounded p-1 hover:bg-black/10" aria-label="Dismiss" data-testid="upgrade-banner-dismiss"><X size={16} /></button>
    </div>
  );
}

export function AboutPage() {
  const { data } = useApi("/about");
  if (!data) return null;
  return (
    <div data-testid="admin-about">
      <div className="mb-6"><p className="eyebrow text-gold">Platform</p><h1 className="font-serif-display text-3xl font-bold text-crimson">About & release notes</h1>
        <p className="mt-1 text-sm text-muted-foreground">Version <b className="font-mono-accent text-foreground" data-testid="about-version">v{data.version}</b>{data.release?.deployed_at && ` · deployed ${data.release.deployed_at.slice(0, 16).replace("T", " ")} UTC`}{data.release?.kind && ` · ${data.release.kind}`}</p></div>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <article className="glass prose max-w-none rounded-2xl p-6 text-sm prose-headings:font-serif-display prose-headings:text-crimson" data-testid="about-notes" dangerouslySetInnerHTML={{ __html: md(data.notes || "No release notes for this version.") }} />
        <aside className="space-y-4">
          <div className="glass rounded-2xl p-4"><h3 className="font-serif-display text-lg font-bold text-crimson">Deploy history</h3>
            <ul className="mt-2 space-y-1 text-xs" data-testid="about-history">{(data.history || []).map((r) => <li key={`${r.version}-${r.deployed_at}`} className="flex justify-between gap-2"><span className="font-mono-accent">v{r.version} <span className="text-muted-foreground">{r.kind}</span></span><span className="text-muted-foreground">{r.deployed_at.slice(0, 16).replace("T", " ")}</span></li>)}</ul></div>
          <div className="glass rounded-2xl p-4"><h3 className="font-serif-display text-lg font-bold text-crimson">Data safety</h3>
            <p className="mt-1 text-xs text-muted-foreground">Publishing is an update: live members, payments and records are never replaced. Seeding only inserts missing reference data; migrations are forward-only and additive. Applied migrations: <span className="font-mono-accent">{(data.migrations || []).join(", ") || "none"}</span>.</p>
            <p className="mt-2 text-xs text-muted-foreground">Minor/patch versions publish silently. A <b>major</b> version is an upgrade — officers are notified and shown this page.</p></div>
        </aside>
      </div>
      <Button variant="ghost" size="sm" className="mt-4" onClick={() => api.post("/about/seen")} data-testid="about-ack">Mark release notes as read</Button>
    </div>
  );
}
